import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Modal } from '../components/ui/Modal';
import { StatCard } from '../components/ui/StatCard';
import { StatusBadge } from '../components/ui/StatusBadge';
import { PriorityWidget, PriorityItem } from '../components/ui/PriorityWidget';
import { Timeline, TimelineEvent } from '../components/ui/Timeline';
import { ClientStatusBanner } from '../components/ClientStatusBanner';
import { PermissionGate } from '../components/PermissionGate';
import { useClientStatus } from '../hooks/useClientStatus';
import { useRBAC } from '../contexts/RBACContext';
import { mockData } from '../services/mockData';
import { Building2, MapPin, Phone, Mail, Globe, FileText, Users, Receipt, Activity, AlertTriangle, AlertCircle, Clock, Wrench, GraduationCap, Target, Ticket, Plus, ArrowRight, Edit, CheckCircle2, FileCheck, Wallet, History, ArrowLeft, Trash2, Eye } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function ClientDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    isActive
  } = useClientStatus();
  const {
    canEdit
  } = useRBAC();
  const [client, setClient] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // Modal states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showContractsModal, setShowContractsModal] = useState(false);
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [showWorkersModal, setShowWorkersModal] = useState(false);
  const [showOperationsModal, setShowOperationsModal] = useState(false);
  const [showDeadlinesModal, setShowDeadlinesModal] = useState(false);
  const [showNCModal, setShowNCModal] = useState(false);
  const [showTicketsModal, setShowTicketsModal] = useState(false);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  // Mock loading data
  useEffect(() => {
    const foundClient = mockData.getClients().find(c => c.id === Number(id)) || mockData.getClients()[0];
    setClient(foundClient);
    setLoading(false);
  }, [id]);
  if (loading) return <Layout data-id="element-555">
        <div className="p-8 text-center" data-id="element-556">Caricamento...</div>
      </Layout>;
  if (!client) return <Layout data-id="element-557">
        <div className="p-8 text-center" data-id="element-558">Cliente non trovato</div>
      </Layout>;
  const handleDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setShowDeleteDialog(false);
      navigate('/clients');
    }, 1500);
  };
  // Mock Priority Items
  const priorityItems: PriorityItem[] = [{
    id: '1',
    type: 'danger',
    title: 'Contratto in scadenza',
    description: 'Il contratto "Sicurezza Annuale" scade tra 15 giorni.',
    actionLabel: 'Rinnova',
    onAction: () => setShowContractsModal(true)
  }, {
    id: '2',
    type: 'warning',
    title: 'Fattura insoluta',
    description: 'Fattura F-2024/045 scaduta da 5 giorni.',
    actionLabel: 'Sollecita',
    onAction: () => setShowInvoicesModal(true)
  }, {
    id: '3',
    type: 'info',
    title: 'Formazione in scadenza',
    description: '3 lavoratori hanno corsi in scadenza questo mese.',
    actionLabel: 'Vedi corsi',
    onAction: () => setShowTrainingModal(true)
  }];
  // Mock Timeline Events
  const timelineEvents: TimelineEvent[] = [{
    id: '1',
    title: 'Nuovo ticket aperto',
    description: 'Richiesta supporto tecnico',
    date: '2 ore fa',
    icon: Ticket,
    color: 'orange'
  }, {
    id: '2',
    title: 'Fattura emessa',
    description: 'F-2024/089 inviata al cliente',
    date: 'Ieri',
    icon: Receipt,
    color: 'blue'
  }, {
    id: '3',
    title: 'Sopralluogo completato',
    description: 'Verifica periodica sicurezza',
    date: '2 giorni fa',
    icon: CheckCircle2,
    color: 'green'
  }, {
    id: '4',
    title: 'Contratto rinnovato',
    description: 'Sicurezza Annuale 2024',
    date: '1 settimana fa',
    icon: FileCheck,
    color: 'purple'
  }];
  // Mock data for modals
  const mockWorkers = [{
    id: 1,
    name: 'Mario Rossi',
    role: 'Operaio',
    trainingStatus: 'valid'
  }, {
    id: 2,
    name: 'Laura Bianchi',
    role: 'Impiegata',
    trainingStatus: 'valid'
  }, {
    id: 3,
    name: 'Giuseppe Verdi',
    role: 'Tecnico',
    trainingStatus: 'expiring'
  }, {
    id: 4,
    name: 'Anna Neri',
    role: 'Responsabile',
    trainingStatus: 'expired'
  }, {
    id: 5,
    name: 'Paolo Gialli',
    role: 'Operaio',
    trainingStatus: 'valid'
  }, {
    id: 6,
    name: 'Francesca Blu',
    role: 'Segretaria',
    trainingStatus: 'valid'
  }, {
    id: 7,
    name: 'Marco Viola',
    role: 'Magazziniere',
    trainingStatus: 'expiring'
  }, {
    id: 8,
    name: 'Elena Rosa',
    role: 'Contabile',
    trainingStatus: 'valid'
  }, {
    id: 9,
    name: 'Luca Arancio',
    role: 'Operaio',
    trainingStatus: 'expired'
  }, {
    id: 10,
    name: 'Sara Verde',
    role: 'Tecnico',
    trainingStatus: 'valid'
  }, {
    id: 11,
    name: 'Andrea Marrone',
    role: 'Operaio',
    trainingStatus: 'valid'
  }, {
    id: 12,
    name: 'Giulia Grigio',
    role: 'Impiegata',
    trainingStatus: 'expiring'
  }];
  const mockContracts = [{
    id: 1,
    name: 'Sicurezza Annuale 2024',
    status: 'active',
    value: '€ 2.400',
    expiry: '15/12/2024'
  }, {
    id: 2,
    name: 'HACCP Base',
    status: 'expiring',
    value: '€ 800',
    expiry: '30/06/2024'
  }, {
    id: 3,
    name: 'Formazione Obbligatoria',
    status: 'active',
    value: '€ 1.200',
    expiry: '31/12/2024'
  }];
  const mockInvoices = [{
    id: 1,
    number: 'F-2024/089',
    amount: '€ 1.550,00',
    status: 'pending',
    date: '15/05/2024'
  }, {
    id: 2,
    number: 'F-2024/045',
    amount: '€ 850,00',
    status: 'overdue',
    date: '01/04/2024'
  }, {
    id: 3,
    number: 'F-2024/023',
    amount: '€ 2.400,00',
    status: 'paid',
    date: '15/02/2024'
  }];
  return <Layout data-id="element-559">
      {/* Back Button with Breadcrumb */}
      <div className="mb-6" data-id="element-560">
        <button onClick={() => navigate('/clients')} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-seform-navy transition-colors group" data-id="element-561">
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" data-id="element-562" />
          <span data-id="element-563">Anagrafica clienti</span>
        </button>
      </div>

      {/* Header Section */}
      <div className="mb-6" data-id="element-564">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4" data-id="element-565">
          <div className="flex items-start gap-4" data-id="element-566">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-seform-navy to-blue-900 flex items-center justify-center text-white font-bold text-2xl shadow-lg" data-id="element-567">
              {client.name.substring(0, 2).toUpperCase()}
            </div>
            <div data-id="element-568">
              <div className="flex items-center gap-3" data-id="element-569">
                <h1 className="text-3xl font-bold text-seform-navy" data-id="element-570">
                  {client.name}
                </h1>
                <StatusBadge status={client.status === 'Attivo' ? 'active' : client.status === 'Prospect' ? 'pending' : 'inactive'} data-id="element-571" />
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500" data-id="element-572">
                <div className="flex items-center gap-1.5" data-id="element-573">
                  <MapPin className="h-4 w-4 text-slate-400" data-id="element-574" />
                  {client.zone || 'Arezzo'}
                </div>
                <div className="flex items-center gap-1.5" data-id="element-575">
                  <Building2 className="h-4 w-4 text-slate-400" data-id="element-576" />
                  P.IVA: 01234567890
                </div>
                <div className="flex items-center gap-1.5" data-id="element-577">
                  <Users className="h-4 w-4 text-slate-400" data-id="element-578" />
                  Ref: {client.referent}
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2" data-id="element-579">
            <PermissionGate module="tickets" action="create" data-id="element-580">
              <Button variant="flat" startContent={<Ticket className="h-4 w-4" data-id="element-582" />} onClick={() => setShowTicketsModal(true)} data-id="element-581">
                Ticket
              </Button>
            </PermissionGate>
            <PermissionGate module="operations" action="create" data-id="element-583">
              <Button variant="flat" startContent={<Activity className="h-4 w-4" data-id="element-585" />} onClick={() => setShowOperationsModal(true)} data-id="element-584">
                Attività
              </Button>
            </PermissionGate>
            <PermissionGate module="clients" action="edit" data-id="element-586">
              <Button color="secondary" startContent={<Edit className="h-4 w-4" data-id="element-588" />} data-id="element-587">
                Modifica
              </Button>
            </PermissionGate>
            <PermissionGate module="clients" action="delete" data-id="element-589">
              <Button variant="flat" color="danger" startContent={<Trash2 className="h-4 w-4" data-id="element-591" />} onClick={() => setShowDeleteDialog(true)} data-id="element-590">
                Elimina
              </Button>
            </PermissionGate>
          </div>
        </div>

        {/* Status Banner for Inactive Clients */}
        <ClientStatusBanner clientId={client.id} data-id="element-592" />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8" data-id="element-593">
        {/* Left Column (Main Modules) */}
        <div className="xl:col-span-2 space-y-8" data-id="element-594">
          {/* Priority Widget */}
          <PriorityWidget items={priorityItems} data-id="element-595" />

          {/* Module Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-596">
            {/* Contracts */}
            <Card title="Contratti e preventivi" actions={<Button size="sm" variant="ghost" onClick={() => setShowContractsModal(true)} className="text-base font-medium" data-id="element-598">
                  Vedi tutto
                </Button>} data-id="element-597">
              <div className="space-y-4" data-id="element-599">
                <div className="grid grid-cols-2 gap-4" data-id="element-600">
                  <StatCard title="Attivi" value="3" icon={FileCheck} color="green" data-id="element-601" />
                  <StatCard title="In scadenza" value="1" icon={Clock} color="orange" data-id="element-602" />
                </div>
                <div className="pt-2 border-t border-slate-50" data-id="element-603">
                  <div className="flex justify-between items-center text-sm mb-2" data-id="element-604">
                    <span className="text-slate-600" data-id="element-605">
                      Sicurezza Annuale 2024
                    </span>
                    <Badge variant="success" size="sm" data-id="element-606">
                      Attivo
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center text-sm" data-id="element-607">
                    <span className="text-slate-600" data-id="element-608">HACCP Base</span>
                    <Badge variant="warning" size="sm" data-id="element-609">
                      In scadenza
                    </Badge>
                  </div>
                </div>
                <Button fullWidth variant="flat" size="sm" startContent={<Plus className="h-4 w-4" data-id="element-611" />} className="text-base" data-id="element-610">
                  Nuovo contratto
                </Button>
              </div>
            </Card>

            {/* Invoicing */}
            <Card title="Fatturazione" actions={<Button size="sm" variant="ghost" onClick={() => setShowInvoicesModal(true)} className="text-base font-medium" data-id="element-613">
                  Vedi tutto
                </Button>} data-id="element-612">
              <div className="space-y-4" data-id="element-614">
                <div className="grid grid-cols-2 gap-4" data-id="element-615">
                  <StatCard title="Da incassare" value="€ 2.400" icon={Wallet} color="blue" data-id="element-616" />
                  <StatCard title="Scadute" value="€ 850" icon={AlertCircle} color="red" data-id="element-617" />
                </div>
                <div className="pt-2 border-t border-slate-50" data-id="element-618">
                  <div className="flex justify-between items-center text-sm mb-2" data-id="element-619">
                    <span className="text-slate-600" data-id="element-620">Fatt. 45/2024</span>
                    <span className="font-mono font-medium text-red-600" data-id="element-621">
                      € 850,00
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm" data-id="element-622">
                    <span className="text-slate-600" data-id="element-623">Proforma 12/2024</span>
                    <span className="font-mono font-medium text-slate-900" data-id="element-624">
                      € 1.550,00
                    </span>
                  </div>
                </div>
                <Button fullWidth variant="flat" size="sm" startContent={<Plus className="h-4 w-4" data-id="element-626" />} className="text-base" data-id="element-625">
                  Nuova fattura
                </Button>
              </div>
            </Card>

            {/* Workers - Clickable */}
            <Card title="Lavoratori" actions={<Button size="sm" variant="ghost" onClick={() => setShowWorkersModal(true)} className="text-base font-medium" data-id="element-628">
                  Vedi tutto
                </Button>} data-id="element-627">
              <div className="space-y-4" data-id="element-629">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setShowWorkersModal(true)} data-id="element-630">
                  <div className="flex items-center gap-3" data-id="element-631">
                    <Users className="h-5 w-5 text-seform-navy" data-id="element-632" />
                    <div data-id="element-633">
                      <p className="text-sm font-bold text-seform-navy" data-id="element-634">
                        12 dipendenti
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-635">
                        Censiti a sistema
                      </p>
                    </div>
                  </div>
                  <Button size="sm" variant="light" className="!px-2" data-id="element-636">
                    <ArrowRight className="h-4 w-4" data-id="element-637" />
                  </Button>
                </div>
                <div className="space-y-2" data-id="element-638">
                  <div className="flex justify-between text-sm" data-id="element-639">
                    <span className="text-slate-600" data-id="element-640">Formazione in regola</span>
                    <span className="font-medium text-green-600" data-id="element-641">8</span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-642">
                    <span className="text-slate-600" data-id="element-643">In scadenza/Scaduti</span>
                    <span className="font-medium text-red-600" data-id="element-644">4</span>
                  </div>
                </div>
                <Button fullWidth variant="flat" size="sm" startContent={<Plus className="h-4 w-4" data-id="element-646" />} className="text-base" data-id="element-645">
                  Aggiungi lavoratore
                </Button>
              </div>
            </Card>

            {/* Operations */}
            <Card title="Attività operative" actions={<Button size="sm" variant="ghost" onClick={() => setShowOperationsModal(true)} className="text-base font-medium" data-id="element-648">
                  Vedi tutto
                </Button>} data-id="element-647">
              <div className="space-y-4" data-id="element-649">
                <div className="grid grid-cols-2 gap-4" data-id="element-650">
                  <StatCard title="Aperte" value="5" icon={Activity} color="blue" data-id="element-651" />
                  <StatCard title="Urgenti" value="1" icon={AlertTriangle} color="red" data-id="element-652" />
                </div>
                <div className="pt-2 border-t border-slate-50" data-id="element-653">
                  <div className="flex justify-between items-center text-sm mb-2" data-id="element-654">
                    <span className="text-slate-600" data-id="element-655">
                      Sopralluogo sicurezza
                    </span>
                    <Badge variant="info" size="sm" data-id="element-656">
                      Domani
                    </Badge>
                  </div>
                </div>
                <Button fullWidth variant="flat" size="sm" startContent={<Plus className="h-4 w-4" data-id="element-658" />} className="text-base" data-id="element-657">
                  Nuova attività
                </Button>
              </div>
            </Card>

            {/* Deadlines */}
            <Card title="Scadenziario" actions={<Button size="sm" variant="ghost" onClick={() => setShowDeadlinesModal(true)} className="text-base font-medium" data-id="element-660">
                  Vedi tutto
                </Button>} data-id="element-659">
              <div className="space-y-4" data-id="element-661">
                <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl border border-orange-100" data-id="element-662">
                  <Clock className="h-5 w-5 text-orange-600" data-id="element-663" />
                  <div data-id="element-664">
                    <p className="text-sm font-bold text-orange-700" data-id="element-665">
                      3 scadenze
                    </p>
                    <p className="text-xs text-orange-600" data-id="element-666">
                      Nei prossimi 30 giorni
                    </p>
                  </div>
                </div>
                <ul className="space-y-2" data-id="element-667">
                  <li className="flex justify-between text-sm" data-id="element-668">
                    <span className="text-slate-600" data-id="element-669">Rinnovo DVR</span>
                    <span className="text-slate-900 font-medium" data-id="element-670">
                      15/06/2024
                    </span>
                  </li>
                  <li className="flex justify-between text-sm" data-id="element-671">
                    <span className="text-slate-600" data-id="element-672">Analisi acque</span>
                    <span className="text-slate-900 font-medium" data-id="element-673">
                      20/06/2024
                    </span>
                  </li>
                </ul>
              </div>
            </Card>

            {/* Non Conformities */}
            <Card title="Non conformità" actions={<Button size="sm" variant="ghost" onClick={() => setShowNCModal(true)} className="text-base font-medium" data-id="element-675">
                  Vedi tutto
                </Button>} data-id="element-674">
              <div className="space-y-4" data-id="element-676">
                <div className="flex justify-between items-center" data-id="element-677">
                  <span className="text-sm text-slate-600" data-id="element-678">NC aperte</span>
                  <Badge variant="danger" data-id="element-679">2</Badge>
                </div>
                <div className="flex justify-between items-center" data-id="element-680">
                  <span className="text-sm text-slate-600" data-id="element-681">Miglioramenti</span>
                  <Badge variant="info" data-id="element-682">1</Badge>
                </div>
                <Button fullWidth variant="flat" size="sm" startContent={<Plus className="h-4 w-4" data-id="element-684" />} className="text-base" data-id="element-683">
                  Segnala NC
                </Button>
              </div>
            </Card>
          </div>
        </div>

        {/* Right Column (Sidebar Info) */}
        <div className="space-y-6" data-id="element-685">
          {/* Contact Info Card */}
          <Card title="Contatti" data-id="element-686">
            <div className="space-y-4" data-id="element-687">
              <div className="flex items-center gap-3 text-sm" data-id="element-688">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center" data-id="element-689">
                  <Phone className="h-4 w-4 text-slate-500" data-id="element-690" />
                </div>
                <div data-id="element-691">
                  <p className="text-xs text-slate-500" data-id="element-692">Telefono</p>
                  <p className="font-medium text-slate-900" data-id="element-693">
                    {client.phone || '0575 123456'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm" data-id="element-694">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center" data-id="element-695">
                  <Mail className="h-4 w-4 text-slate-500" data-id="element-696" />
                </div>
                <div data-id="element-697">
                  <p className="text-xs text-slate-500" data-id="element-698">Email</p>
                  <p className="font-medium text-slate-900" data-id="element-699">{client.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm" data-id="element-700">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center" data-id="element-701">
                  <Globe className="h-4 w-4 text-slate-500" data-id="element-702" />
                </div>
                <div data-id="element-703">
                  <p className="text-xs text-slate-500" data-id="element-704">Sito web</p>
                  <p className="font-medium text-slate-900" data-id="element-705">www.azienda.it</p>
                </div>
              </div>
              <div className="pt-4 border-t border-slate-50" data-id="element-706">
                <p className="text-xs font-bold text-seform-navy uppercase mb-2" data-id="element-707">
                  Referenti
                </p>
                <div className="space-y-2" data-id="element-708">
                  <div className="text-sm" data-id="element-709">
                    <span className="text-slate-500" data-id="element-710">Sicurezza:</span>{' '}
                    <span className="font-medium" data-id="element-711">Mario Rossi</span>
                  </div>
                  <div className="text-sm" data-id="element-712">
                    <span className="text-slate-500" data-id="element-713">Amm.ne:</span>{' '}
                    <span className="font-medium" data-id="element-714">Laura Bianchi</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Quick Links - Moved below Contacts */}
          <Card title="Accesso rapido" data-id="element-715">
            <div className="grid grid-cols-2 gap-2" data-id="element-716">
              <Button variant="flat" size="sm" className="justify-start text-base" startContent={<GraduationCap className="h-4 w-4" data-id="element-718" />} onClick={() => setShowTrainingModal(true)} data-id="element-717">
                Formazione
              </Button>
              <Button variant="flat" size="sm" className="justify-start text-base" startContent={<Wrench className="h-4 w-4" data-id="element-720" />} data-id="element-719">
                Macchinari
              </Button>
              <Button variant="flat" size="sm" className="justify-start text-base" startContent={<Target className="h-4 w-4" data-id="element-722" />} data-id="element-721">
                Obiettivi
              </Button>
              <Button variant="flat" size="sm" className="justify-start text-base" startContent={<FileText className="h-4 w-4" data-id="element-724" />} data-id="element-723">
                Documenti
              </Button>
            </div>
          </Card>

          {/* Timeline */}
          <Card title="Storia recente" actions={<Button size="sm" variant="ghost" data-id="element-726">
                <History className="h-4 w-4" data-id="element-727" />
              </Button>} data-id="element-725">
            <Timeline events={timelineEvents} data-id="element-728" />
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Conferma eliminazione" size="sm" data-id="element-729">
        <AnimatePresence mode="wait" data-id="element-730">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-12" data-id="element-731">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-732">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-733" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-734">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-735">
              <div className="space-y-6" data-id="element-736">
                <div className="flex items-start gap-4" data-id="element-737">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0" data-id="element-738">
                    <Trash2 className="h-6 w-6 text-red-600" data-id="element-739" />
                  </div>
                  <div data-id="element-740">
                    <p className="text-slate-700" data-id="element-741">
                      Sei sicuro di voler eliminare l'anagrafica{' '}
                      <strong data-id="element-742">{client.name}</strong>?
                    </p>
                    <p className="text-sm text-slate-500 mt-2" data-id="element-743">
                      Questa azione è irreversibile e rimuoverà tutti i dati
                      associati.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-744">
                  <Button variant="flat" color="default" onClick={() => setShowDeleteDialog(false)} data-id="element-745">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={handleDelete} startContent={<Trash2 className="h-4 w-4" data-id="element-747" />} data-id="element-746">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Workers Modal */}
      <Modal isOpen={showWorkersModal} onClose={() => setShowWorkersModal(false)} title={`Lavoratori - ${client.name}`} size="lg" data-id="element-748">
        <div className="space-y-4 max-h-[60vh] overflow-y-auto" data-id="element-749">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-id="element-750">
            <span className="font-medium text-seform-navy" data-id="element-751">
              Totale dipendenti
            </span>
            <Badge variant="primary" data-id="element-752">{mockWorkers.length}</Badge>
          </div>
          <div className="divide-y divide-slate-100" data-id="element-753">
            {mockWorkers.map(worker => <div key={worker.id} className="flex items-center justify-between py-3" data-id="element-754">
                <div className="flex items-center gap-3" data-id="element-755">
                  <div className="w-8 h-8 rounded-full bg-seform-navy/10 flex items-center justify-center text-seform-navy font-bold text-xs" data-id="element-756">
                    {worker.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div data-id="element-757">
                    <p className="text-sm font-medium text-slate-900" data-id="element-758">
                      {worker.name}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-759">{worker.role}</p>
                  </div>
                </div>
                <Badge variant={worker.trainingStatus === 'valid' ? 'success' : worker.trainingStatus === 'expiring' ? 'warning' : 'danger'} size="sm" data-id="element-760">
                  {worker.trainingStatus === 'valid' ? 'In regola' : worker.trainingStatus === 'expiring' ? 'In scadenza' : 'Scaduto'}
                </Badge>
              </div>)}
          </div>
        </div>
      </Modal>

      {/* Contracts Modal */}
      <Modal isOpen={showContractsModal} onClose={() => setShowContractsModal(false)} title={`Contratti e preventivi - ${client.name}`} size="lg" data-id="element-761">
        <div className="space-y-4 max-h-[60vh] overflow-y-auto" data-id="element-762">
          {mockContracts.map(contract => <div key={contract.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg" data-id="element-763">
              <div data-id="element-764">
                <p className="font-medium text-slate-900" data-id="element-765">{contract.name}</p>
                <p className="text-sm text-slate-500" data-id="element-766">
                  Scadenza: {contract.expiry}
                </p>
              </div>
              <div className="text-right" data-id="element-767">
                <p className="font-mono font-medium text-seform-navy" data-id="element-768">
                  {contract.value}
                </p>
                <Badge variant={contract.status === 'active' ? 'success' : 'warning'} size="sm" data-id="element-769">
                  {contract.status === 'active' ? 'Attivo' : 'In scadenza'}
                </Badge>
              </div>
            </div>)}
        </div>
      </Modal>

      {/* Invoices Modal */}
      <Modal isOpen={showInvoicesModal} onClose={() => setShowInvoicesModal(false)} title={`Fatturazione - ${client.name}`} size="lg" data-id="element-770">
        <div className="space-y-4 max-h-[60vh] overflow-y-auto" data-id="element-771">
          {mockInvoices.map(invoice => <div key={invoice.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg" data-id="element-772">
              <div data-id="element-773">
                <p className="font-medium text-slate-900" data-id="element-774">{invoice.number}</p>
                <p className="text-sm text-slate-500" data-id="element-775">Data: {invoice.date}</p>
              </div>
              <div className="text-right" data-id="element-776">
                <p className={`font-mono font-medium ${invoice.status === 'overdue' ? 'text-red-600' : 'text-seform-navy'}`} data-id="element-777">
                  {invoice.amount}
                </p>
                <Badge variant={invoice.status === 'paid' ? 'success' : invoice.status === 'overdue' ? 'danger' : 'warning'} size="sm" data-id="element-778">
                  {invoice.status === 'paid' ? 'Pagata' : invoice.status === 'overdue' ? 'Scaduta' : 'In attesa'}
                </Badge>
              </div>
            </div>)}
        </div>
      </Modal>

      {/* Operations Modal */}
      <Modal isOpen={showOperationsModal} onClose={() => setShowOperationsModal(false)} title={`Attività operative - ${client.name}`} size="lg" data-id="element-779">
        <div className="space-y-4 max-h-[60vh] overflow-y-auto" data-id="element-780">
          <div className="p-4 bg-slate-50 rounded-lg" data-id="element-781">
            <p className="font-medium text-slate-900" data-id="element-782">Sopralluogo sicurezza</p>
            <p className="text-sm text-slate-500" data-id="element-783">Pianificato per domani</p>
            <Badge variant="info" size="sm" className="mt-2" data-id="element-784">
              In programma
            </Badge>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg" data-id="element-785">
            <p className="font-medium text-slate-900" data-id="element-786">Verifica impianti</p>
            <p className="text-sm text-slate-500" data-id="element-787">Completato il 10/05/2024</p>
            <Badge variant="success" size="sm" className="mt-2" data-id="element-788">
              Completato
            </Badge>
          </div>
        </div>
      </Modal>

      {/* Deadlines Modal */}
      <Modal isOpen={showDeadlinesModal} onClose={() => setShowDeadlinesModal(false)} title={`Scadenziario - ${client.name}`} size="lg" data-id="element-789">
        <div className="space-y-4 max-h-[60vh] overflow-y-auto" data-id="element-790">
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-100" data-id="element-791">
            <div className="flex justify-between items-center" data-id="element-792">
              <p className="font-medium text-orange-900" data-id="element-793">Rinnovo DVR</p>
              <span className="text-sm font-medium text-orange-700" data-id="element-794">
                15/06/2024
              </span>
            </div>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg" data-id="element-795">
            <div className="flex justify-between items-center" data-id="element-796">
              <p className="font-medium text-slate-900" data-id="element-797">Analisi acque</p>
              <span className="text-sm font-medium text-slate-700" data-id="element-798">
                20/06/2024
              </span>
            </div>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg" data-id="element-799">
            <div className="flex justify-between items-center" data-id="element-800">
              <p className="font-medium text-slate-900" data-id="element-801">Verifica estintori</p>
              <span className="text-sm font-medium text-slate-700" data-id="element-802">
                30/06/2024
              </span>
            </div>
          </div>
        </div>
      </Modal>

      {/* NC Modal */}
      <Modal isOpen={showNCModal} onClose={() => setShowNCModal(false)} title={`Non conformità - ${client.name}`} size="lg" data-id="element-803">
        <div className="space-y-4 max-h-[60vh] overflow-y-auto" data-id="element-804">
          <div className="p-4 bg-red-50 rounded-lg border border-red-100" data-id="element-805">
            <p className="font-medium text-red-900" data-id="element-806">
              NC-2024/012 - Mancanza DPI
            </p>
            <p className="text-sm text-red-700 mt-1" data-id="element-807">Aperta il 05/05/2024</p>
            <Badge variant="danger" size="sm" className="mt-2" data-id="element-808">
              Aperta
            </Badge>
          </div>
          <div className="p-4 bg-red-50 rounded-lg border border-red-100" data-id="element-809">
            <p className="font-medium text-red-900" data-id="element-810">
              NC-2024/008 - Segnaletica mancante
            </p>
            <p className="text-sm text-red-700 mt-1" data-id="element-811">Aperta il 20/04/2024</p>
            <Badge variant="danger" size="sm" className="mt-2" data-id="element-812">
              Aperta
            </Badge>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100" data-id="element-813">
            <p className="font-medium text-blue-900" data-id="element-814">
              Miglioramento - Ottimizzazione percorsi
            </p>
            <p className="text-sm text-blue-700 mt-1" data-id="element-815">Proposto il 15/04/2024</p>
            <Badge variant="info" size="sm" className="mt-2" data-id="element-816">
              In valutazione
            </Badge>
          </div>
        </div>
      </Modal>

      {/* Tickets Modal */}
      <Modal isOpen={showTicketsModal} onClose={() => setShowTicketsModal(false)} title={`Ticket - ${client.name}`} size="lg" data-id="element-817">
        <div className="space-y-4 max-h-[60vh] overflow-y-auto" data-id="element-818">
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-100" data-id="element-819">
            <p className="font-medium text-orange-900" data-id="element-820">
              TK-2024/089 - Richiesta supporto tecnico
            </p>
            <p className="text-sm text-orange-700 mt-1" data-id="element-821">Aperto 2 ore fa</p>
            <Badge variant="warning" size="sm" className="mt-2" data-id="element-822">
              In lavorazione
            </Badge>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg" data-id="element-823">
            <p className="font-medium text-slate-900" data-id="element-824">
              TK-2024/078 - Aggiornamento documentazione
            </p>
            <p className="text-sm text-slate-500 mt-1" data-id="element-825">Chiuso il 10/05/2024</p>
            <Badge variant="success" size="sm" className="mt-2" data-id="element-826">
              Risolto
            </Badge>
          </div>
        </div>
      </Modal>

      {/* Training Modal */}
      <Modal isOpen={showTrainingModal} onClose={() => setShowTrainingModal(false)} title={`Formazione - ${client.name}`} size="lg" data-id="element-827">
        <div className="space-y-4 max-h-[60vh] overflow-y-auto" data-id="element-828">
          <div className="p-4 bg-green-50 rounded-lg border border-green-100" data-id="element-829">
            <p className="font-medium text-green-900" data-id="element-830">Corso antincendio base</p>
            <p className="text-sm text-green-700 mt-1" data-id="element-831">
              8 partecipanti - Valido fino al 31/12/2024
            </p>
            <Badge variant="success" size="sm" className="mt-2" data-id="element-832">
              In regola
            </Badge>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-100" data-id="element-833">
            <p className="font-medium text-orange-900" data-id="element-834">Primo soccorso</p>
            <p className="text-sm text-orange-700 mt-1" data-id="element-835">
              3 partecipanti - Scade il 30/06/2024
            </p>
            <Badge variant="warning" size="sm" className="mt-2" data-id="element-836">
              In scadenza
            </Badge>
          </div>
          <div className="p-4 bg-red-50 rounded-lg border border-red-100" data-id="element-837">
            <p className="font-medium text-red-900" data-id="element-838">
              Formazione specifica rischio alto
            </p>
            <p className="text-sm text-red-700 mt-1" data-id="element-839">
              1 partecipante - Scaduto il 15/04/2024
            </p>
            <Badge variant="danger" size="sm" className="mt-2" data-id="element-840">
              Scaduto
            </Badge>
          </div>
        </div>
      </Modal>
    </Layout>;
}